import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { api } from '../services/api'
import { Search, ChevronLeft, ChevronRight, Users, Phone, MapPin, Tag, Plus, Edit, Trash2, Filter, X } from 'lucide-react'
import PersonModal from '../components/PersonModal'
import { useAuth } from '../contexts/AuthContext'

const PeopleList = () => {
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [tags, setTags] = useState([])
  
  // Estados para filtros avançados
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [filters, setFilters] = useState({
    cidade: '',
    bairro: '',
    genero: '',
    estado_civil: '',
    profissao: '',
    regiao: '',
    tags: [],
    assessores: '',
    mensagem: '',
    tem_telefone: ''
  })
  
  // Estados para o modal
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const { permissions } = useAuth()

  useEffect(() => {
    loadPeople()
    loadTags()
  }, [currentPage, perPage, filters])

  useEffect(() => {
    // Debounce da busca
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1) // Reset para primeira página
      loadPeople()
    }, 500)

    setSearchTimeout(timeout)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [search])

  const loadPeople = async () => {
    try {
      setLoading(true)
      const params = {
        page: currentPage,
        per_page: perPage,
        ...(search && { search }),
        ...(filters.cidade && { cidade: filters.cidade }),
        ...(filters.bairro && { bairro: filters.bairro }),
        ...(filters.genero && { genero: filters.genero }),
        ...(filters.estado_civil && { estado_civil: filters.estado_civil }),
        ...(filters.profissao && { profissao: filters.profissao }),
        ...(filters.regiao && { regiao: filters.regiao }),
        ...(filters.tags.length > 0 && { tags: filters.tags.map(t => t.value).join(',') }),
        ...(filters.assessores && { assessores: filters.assessores }),
        ...(filters.mensagem && { mensagem: filters.mensagem }),
        ...(filters.tem_telefone && { tem_telefone: filters.tem_telefone })
      }

      const response = await api.get('/pessoas/list.php', params)
      
      if (response.ok) {
        setPeople(response.data || [])
        setTotal(response.total || 0)
        setError(null)
      } else {
        if (response.status === 401) {
          setError('Sessão expirada. Faça login novamente.')
        } else {
          setError(response.message || 'Erro ao carregar pessoas')
        }
        setPeople([])
        setTotal(0)
      }
    } catch (err) {
      console.error('Erro ao carregar pessoas:', err)
      setError('Erro de conexão')
      setPeople([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const loadTags = async () => {
    try {
      const response = await api.get('/tags/list.php');
      if (response.ok && Array.isArray(response.data)) {
        const formattedTags = response.data.map(tag => ({
          value: tag.id_tag, // Usar o ID da tag como valor
          label: tag.tag
        }));
        setTags(formattedTags);
      } else {
        console.error('Erro ao carregar tags:', response.message || 'Formato de dados inválido');
        setTags([]);
      }
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      setTags([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
  }

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
    setCurrentPage(1) // Reset para primeira página
  }

  const handleTagsFilterChange = (selectedOptions) => {
    setFilters(prev => ({ ...prev, tags: selectedOptions || [] }))
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setFilters({
      cidade: '',
      bairro: '',
      genero: '',
      estado_civil: '',
      profissao: '',
      regiao: '',
      tags: [],
      assessores: '',
      mensagem: '',
      tem_telefone: ''
    })
    setCurrentPage(1)
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value)
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset para primeira página
  }

  // Funções CRUD
  const handleCreate = () => {
    setSelectedPerson(null)
    setModalOpen(true)
  }

  const handleEdit = (person) => {
    setSelectedPerson(person)
    setModalOpen(true)
  }

  const handleSave = async (formData) => {
    setModalLoading(true)
    try {
      if (selectedPerson) {
        // Editar pessoa existente
        const response = await api.put(`/pessoas/update.php?id=${selectedPerson.id_pessoa}`, formData)
        if (response.ok) {
          // Recarregar lista
          loadPeople()
        } else {
          throw new Error(response.message || 'Erro ao atualizar pessoa')
        }
      } else {
        // Criar nova pessoa
        const response = await api.post('/pessoas/create.php', formData)
        if (response.ok) {
          // Recarregar lista
          loadPeople()
        } else {
          throw new Error(response.message || 'Erro ao criar pessoa')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar pessoa:', error)
      alert(`Erro: ${error.message}`)
      throw error
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (idPessoa) => {
    try {
      const response = await api.delete(`/pessoas/delete.php?id=${idPessoa}`)
      if (response.ok) {
        // Recarregar lista
        loadPeople()
      } else {
        throw new Error(response.message || 'Erro ao excluir pessoa')
      }
    } catch (error) {
      console.error('Erro ao excluir pessoa:', error)
      alert(`Erro: ${error.message}`)
      throw error
    }
  }

  const totalPages = Math.ceil(total / perPage)
  const startItem = (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, total)

  const formatCPF = (cpf) => {
    if (!cpf) return '-'
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  const formatPhone = (phone) => {
    if (!phone) return '-'
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  if (error && error.includes('Sessão expirada')) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-red-500 mb-4">
          <Users className="h-12 w-12" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Sessão Expirada
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="page-transition">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Pessoas
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Gerencie o cadastro de pessoas no sistema
            </p>
          </div>
          {(permissions?.editar_pessoa === 'sim') && (
            <button
              onClick={handleCreate}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Pessoa
            </button>
          )}
        </div>
      </div>

      {/* Filtros e busca */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, CPF, cidade..."
                value={search}
                onChange={handleSearchChange}
                className="input pl-10 w-full"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`btn-outline ${showAdvancedFilters ? 'bg-primary text-white border-primary' : ''}`}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros Avançados
            </button>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Por página:
            </label>
            <select
              value={perPage}
              onChange={handlePerPageChange}
              className="input w-20"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Filtros avançados */}
        {showAdvancedFilters && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cidade
                </label>
                <input
                  type="text"
                  value={filters.cidade}
                  onChange={(e) => handleFilterChange('cidade', e.target.value)}
                  className="input w-full"
                  placeholder="Filtrar por cidade"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bairro
                </label>
                <input
                  type="text"
                  value={filters.bairro}
                  onChange={(e) => handleFilterChange('bairro', e.target.value)}
                  className="input w-full"
                  placeholder="Filtrar por bairro"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gênero
                </label>
                <select
                  value={filters.genero}
                  onChange={(e) => handleFilterChange('genero', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Todos</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="O">Outro</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estado Civil
                </label>
                <select
                  value={filters.estado_civil}
                  onChange={(e) => handleFilterChange('estado_civil', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Todos</option>
                  <option value="solteiro">Solteiro(a)</option>
                  <option value="casado">Casado(a)</option>
                  <option value="divorciado">Divorciado(a)</option>
                  <option value="viuvo">Viúvo(a)</option>
                  <option value="uniao_estavel">União Estável</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Profissão
                </label>
                <input
                  type="text"
                  value={filters.profissao}
                  onChange={(e) => handleFilterChange('profissao', e.target.value)}
                  className="input w-full"
                  placeholder="Filtrar por profissão"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Região
                </label>
                <select
                  value={filters.regiao}
                  onChange={(e) => handleFilterChange('regiao', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Todas</option>
                  <option value="norte">Norte</option>
                  <option value="nordeste">Nordeste</option>
                  <option value="centro_oeste">Centro-Oeste</option>
                  <option value="sudeste">Sudeste</option>
                  <option value="sul">Sul</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
                <Select
                  isMulti
                  name="tags"
                  options={tags}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={filters.tags}
                  onChange={handleTagsFilterChange}
                  placeholder="Selecione as tags"
                  noOptionsMessage={() => 'Nenhuma tag encontrada'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assessores
                </label>
                <input
                  type="text"
                  value={filters.assessores}
                  onChange={(e) => handleFilterChange('assessores', e.target.value)}
                  className="input w-full"
                  placeholder="IDs dos assessores, separados por vírgula"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status Mensagem
                </label>
                <select
                  value={filters.mensagem}
                  onChange={(e) => handleFilterChange('mensagem', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Todos</option>
                  <option value="1">Enviada</option>
                  <option value="0">Não Enviada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Possui Telefone
                </label>
                <select
                  value={filters.tem_telefone}
                  onChange={(e) => handleFilterChange('tem_telefone', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Todos</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="btn-outline w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lista de pessoas */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-red-500 mb-4">
              <Users className="h-12 w-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Erro ao carregar dados
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {error}
            </p>
            <button
              onClick={loadPeople}
              className="btn-primary"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      CPF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Profissão
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cargo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {people.length > 0 ? (
                    people.map((person) => (
                      <tr key={person.id_pessoa} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {person.id_pessoa}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {person.nome}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatCPF(person.cpf)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            {formatPhone(person.telefone)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                            {person.cidade || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {person.nome_profissao || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {person.nome_cargo || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {person.tags ? (
                            <div className="flex items-center">
                              <Tag className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="truncate max-w-32">{person.tags}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-2">
                            {(permissions?.editar_pessoa === 'sim') && (
                              <>
                                <button
                                  onClick={() => handleEdit(person)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm('Tem certeza que deseja excluir esta pessoa?')) {
                                      handleDelete(person.id_pessoa)
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  title="Excluir"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="text-gray-500 dark:text-gray-400">
                          <Users className="mx-auto h-12 w-12 mb-4" />
                          <p className="text-lg font-medium">Nenhuma pessoa encontrada</p>
                          <p className="text-sm">
                            {(search || Object.values(filters).some(f => f)) ? 'Tente ajustar os filtros de busca' : 'Não há pessoas cadastradas no sistema'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {total > 0 && (
              <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Mostrando <span className="font-medium">{startItem}</span> a{' '}
                      <span className="font-medium">{endItem}</span> de{' '}
                      <span className="font-medium">{total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        // Mostrar apenas algumas páginas para não sobrecarregar
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900/20 dark:border-primary-400 dark:text-primary-400'
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        } else if (
                          page === currentPage - 3 ||
                          page === currentPage + 3
                        ) {
                          return (
                            <span
                              key={page}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              ...
                            </span>
                          )
                        }
                        return null
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Pessoa */}
      <PersonModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        person={selectedPerson}
        onSave={handleSave}
        onDelete={handleDelete}
        canEdit={permissions?.editar_pessoa === 'sim'}
      />
    </div>
  )
}

export default PeopleList
